import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

// ── Mismos FILTROS que CxC ────────────────────────────────────────────────────
const FILTROS = [
  { value: "debiendo", label: "Con saldo pendiente", icon: "fa-exclamation-circle" },
  { value: "aldia",    label: "Al día",              icon: "fa-check-circle"       },
  { value: "todos",    label: "Todos",               icon: "fa-list"               },
];

// ── Agrupar CuentaXPagar por proveedor (equivalente a useCxcFiliales) ─────────
function agruparPorProveedor(items, filtro, search) {
  const map = {};
  for (const item of items) {
    const fac    = item.factura_cxp;
    const key    = fac?.cuenta_proveedor?.id ?? "sin-proveedor";
    const nombre = fac?.cuenta_proveedor?.nombre ?? "Sin proveedor";
    if (!map[key]) {
      map[key] = { id:key, nombre, totalMonto:0, totalPagado:0, totalSaldo:0, nFacturas:0 };
    }
    map[key].totalMonto  += Number(item.monto  || 0);
    map[key].totalPagado += Number(item.pagado || 0);
    map[key].totalSaldo  += Number(item.saldo  || 0);
    map[key].nFacturas++;
  }
  let filas = Object.values(map);

  // Filtro igual que CxC
  if (filtro === "debiendo") filas = filas.filter(r => r.totalSaldo > 0);
  if (filtro === "aldia")    filas = filas.filter(r => r.totalSaldo <= 0);

  // Search
  if (search.trim())
    filas = filas.filter(r => r.nombre.toLowerCase().includes(search.toLowerCase()));

  return filas.sort((a, b) => b.totalSaldo - a.totalSaldo);
}

// ── Card de resumen — exactamente igual que SummaryCard de CxC ────────────────
function SummaryCard({ label, value, icon, color, isCurrency = true }) {
  return (
    <div style={{ background:"#1f2937", border:"1px solid #374151",
      borderRadius:10, padding:"16px 20px",
      display:"flex", alignItems:"center", gap:14 }}>
      <div style={{ width:42, height:42, borderRadius:10, flexShrink:0,
        background:`${color}18`, border:`1px solid ${color}30`,
        display:"flex", alignItems:"center", justifyContent:"center" }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize:"1.1rem" }}/>
      </div>
      <div>
        <p style={{ margin:0, color:"#6b7280", fontSize:"0.72rem",
          fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>
          {label}
        </p>
        <p style={{ margin:"2px 0 0", color:"#f9fafb",
          fontSize:"1.1rem", fontWeight:700 }}>
          {isCurrency ? `₡${formatCurrency(value)}` : value}
        </p>
      </div>
    </div>
  );
}

// ── Fila — igual que CxCRow pero para proveedor ────────────────────────────────
function CxPRow({ row }) {
  const saldo   = row.totalSaldo;
  const alDia   = saldo <= 0;
  const parcial = !alDia && row.totalPagado > 0;

  return (
    <tr>
      {/* Proveedor (equivalente a Filial en CxC) */}
      <td style={{ fontWeight:600, color:"#f9fafb" }}>{row.nombre}</td>

      {/* N° facturas (equivalente a Propietario) */}
      <td>{row.nFacturas} factura{row.nFacturas !== 1 ? "s" : ""}</td>

      {/* Total Facturado */}
      <td>
        <div className="monto-typo" style={{ textAlign:"right", fontWeight:600 }}>
          ₡{formatCurrency(row.totalMonto)}
        </div>
      </td>

      {/* Total Abonado */}
      <td>
        <div  className="monto-typo" style={{ textAlign:"right",
          color: row.totalPagado > 0 ? "#4ade80" : "#9ca3af" }}>
          ₡{formatCurrency(row.totalPagado)}
        </div>
      </td>

      {/* Saldo Acumulado */}
      <td>
        <div  className="monto-typo" style={{ textAlign:"right",
          fontWeight:600, color: alDia ? "#4ade80" : "#f87171" }}>
          ₡{formatCurrency(Math.abs(saldo))}
          {saldo < 0 && (
            <span style={{ fontSize:"0.72rem", marginLeft:4 }}>a favor</span>
          )}
        </div>
      </td>

      {/* Estado — idéntico a CxC */}
      <td className="text-center">
        <span style={{
          display:"inline-flex", alignItems:"center", gap:5,
          padding:"3px 10px", borderRadius:20,
          fontSize:"0.74rem", fontWeight:600,
          background: alDia
            ? "rgba(74,222,128,0.1)"
            : parcial ? "rgba(251,191,36,0.1)" : "rgba(248,113,113,0.1)",
          color: alDia ? "#4ade80" : parcial ? "#fbbf24" : "#f87171",
          border: `1px solid ${alDia
            ? "rgba(74,222,128,0.25)"
            : parcial ? "rgba(251,191,36,0.25)" : "rgba(248,113,113,0.25)"}`,
        }}>
          <i className={`fa-solid ${alDia?"fa-check":parcial?"fa-clock":"fa-exclamation"}`}
             style={{ fontSize:"0.65rem" }}/>
          {alDia ? "Al día" : parcial ? "Parcial" : "Pendiente"}
        </span>
      </td>
    </tr>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListCxP() {
  const [filtro,       setFiltro]       = useState("debiendo");
  const [search,       setSearch]       = useState("");
  const [showFilters,  setShowFilters]  = useState(false);
  const [page,         setPage]         = useState(1);
  const [allItems,     setAllItems]     = useState([]);
  const [loading,      setLoading]      = useState(true);

  const PAGE_SIZE = 12;

  // Carga todos los items (sin paginación server-side) para agrupar client-side
  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch("/api/finance/cuentaXPagar/?page_size=500")
      .then(r => r.json())
      .then(d => setAllItems(d.results ?? d ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas = agruparPorProveedor(allItems, filtro, search);

  const totalPages = Math.ceil(filas.length / PAGE_SIZE);
  const filasPage  = filas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleSearch(val) { setSearch(val); setPage(1); }
  function handleFiltro(val) { setFiltro(val); setPage(1); }

  // Totales (antes de paginar) — igual que CxC
  const totalSaldo = filas.reduce((a, r) => a + r.totalSaldo, 0);
  const totalMonto = filas.reduce((a, r) => a + r.totalMonto, 0);

  return (
    <>
      {/* ── Breadcrumb ── */}
            <span className={invoiceStyles.inactive}>
              <Link to="/">
                <span className={invoiceStyles.inactive}>Inicio /</span>
              </Link>
              <span className={invoiceStyles.active}> Cuentas por Pagar</span>
            </span>

      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom:6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">Gestión de CxP</h2>
          <p className="mb-3" style={{color:"#9ca3af"}}>Estado de deuda por proveedor del condominio</p>

        </div>
      </div>

      {/* ── Cards resumen — idéntico a CxC ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)",
        gap:12, marginBottom:20 }}>
        <SummaryCard
          label="Proveedores con saldo pendiente"
          value={filas.filter(r => r.totalSaldo > 0).length}
          icon="fa-building-circle-exclamation"
          color="#f97316"
          isCurrency={false}
        />
        <SummaryCard
          label="Total facturado (filtro)"
          value={totalMonto}
          icon="fa-file-invoice-dollar"
          color="#60a5fa"
        />
        <SummaryCard
          label="Total pendiente (filtro)"
          value={totalSaldo}
          icon="fa-money-bill-wave"
          color={totalSaldo > 0 ? "#f87171" : "#4ade80"}
        />
      </div>

      {/* ── Tabla principal ── */}
      <div className={invoiceStyles["table-container"]}>

        {/* Barra búsqueda + filtros — idéntico a CxC */}
        <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between`}>
          <div className={`${invoiceStyles["input-header"]} d-flex gap-2`}>
            <i className="fa-solid fa-magnifying-glass"
               style={{ color:loading?"#f97316":undefined, transition:"color 0.2s" }}/>
            <input type="text" placeholder="Buscar proveedor…"
              value={search} onChange={e => handleSearch(e.target.value)}/>
            {search && (
              <button onClick={() => handleSearch("")}
                style={{ background:"none", border:"none", color:"#6b7280",
                  cursor:"pointer", fontSize:"0.8rem" }}>✕</button>
            )}
          </div>
          <div className={`${invoiceStyles["actions-buttons-header"]} d-flex gap-2`}>
            <button
              className={`btn ${invoiceStyles["btn-filter"]} d-flex gap-2 align-items-center`}
              onClick={() => setShowFilters(v => !v)}>
              <i className="fa-solid fa-filter"/>
              {FILTROS.find(f => f.value === filtro)?.label ?? "Filtrar"}
            </button>
          </div>
        </div>

        {/* Panel filtros — idéntico a CxC */}
        {showFilters && (
          <div style={{ padding:"12px 16px", borderBottom:"1px solid #374151",
            display:"flex", gap:10, flexWrap:"wrap" }}>
            {FILTROS.map(f => (
              <button key={f.value}
                onClick={() => { handleFiltro(f.value); setShowFilters(false); }}
                style={{ padding:"6px 14px", borderRadius:8,
                  border: filtro===f.value ? "1px solid #f97316" : "1px solid #374151",
                  background: filtro===f.value ? "rgba(249,115,22,0.15)" : "transparent",
                  color: filtro===f.value ? "#f97316" : "#9ca3af",
                  fontSize:"0.83rem", fontWeight: filtro===f.value ? 600 : 400,
                  cursor:"pointer", display:"flex", alignItems:"center",
                  gap:6, transition:"all 0.15s" }}>
                <i className={`fa-solid ${f.icon}`} style={{ fontSize:"0.75rem" }}/>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Facturas</th>
                <th><div style={{ textAlign:"right" }}>Total Facturado</div></th>
                <th><div style={{ textAlign:"right" }}>Total Abonado</div></th>
                <th><div style={{ textAlign:"right" }}>Saldo Acumulado</div></th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign:"center", padding:32, color:"#9ca3af" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight:8, color:"#f97316" }}/>
                  Cargando cuentas por pagar…
                </td></tr>
              ) : filasPage.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign:"center", padding:32, color:"#6b7280" }}>
                  <i className="fa-solid fa-inbox"
                     style={{ marginRight:8, fontSize:"1.2rem" }}/>
                  No hay proveedores con el filtro seleccionado.
                </td></tr>
              ) : (
                filasPage.map(row => <CxPRow key={row.id} row={row}/>)
              )}
            </tbody>
          </table>
        </div>

        {/* Footer paginación — idéntico a CxC */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color:"#f97316", fontSize:"0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:5 }}/>
                Cargando…
              </span>
            ) : (
              <>
                <span>{filas.length}</span> proveedor(es) · Página{" "}
                <span>{page}</span> de <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page<=1||loading} onClick={() => setPage(p=>p-1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              Anterior
            </button>
            <button disabled={page>=totalPages||loading} onClick={() => setPage(p=>p+1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
